import React, { forwardRef, useImperativeHandle, useState, useEffect, useRef } from 'react'
import { Modal } from '../../util/ui'
import { Button } from '../../util/ui_components'
import { fetchByJSON,toUnicode, useCachedData } from '../../util/util'
import { thebeConfigR } from '../../util/thebeConfigs'
import { usePageKernel, attachCells } from '../../util/thebeSession'

const getInitialCode = (data, additional) => `
library(jsonlite)
${additional}
data_json <- '${data}'

# Convert JSON to a list

df_list <- fromJSON(data_json)
all_indices <- unique(unlist(lapply(df_list, names)))
all_indices <- all_indices[order(as.numeric(all_indices))]
columns_list <- lapply(df_list, function(col) {
    col_names <- names(col)
    sapply(all_indices, function(idx) {
        if (idx %in% col_names) {
            val <- col[[idx]]
            if (is.null(val)) NA else val
        } else {
            NA
        }
    })
})
names(columns_list) <- names(df_list)

# Combine the list into a proper data frame
df <- as.data.frame(columns_list, stringsAsFactors = FALSE, check.names = FALSE)
df <- type.convert(df, as.is = TRUE)
`

export default forwardRef(({ dataset, additional }, ref) => {
  let [statusText, setStatusText] = useState('Loading...')
  let [code, setCode] = useState('')
  let [codeVisible, setCodeVisible] = useState(false)
  let codeParent = useRef(null)
  let kernelRef = useRef()
  // one kernel per mounted page, shut down again on unmount
  const getPageKernel = usePageKernel(thebeConfigR)
  let dataFrameRaw = useRef('')

  useEffect(() => {
    if (!code) return
    codeParent.current.innerHTML = ''
    let pre = document.createElement('pre')
    pre.setAttribute('data-executable', 'true')
    pre.setAttribute('data-language', 'R')
    codeParent?.current?.appendChild(pre)
    pre.innerHTML = code
    let isActive = true

    // Only the freshly created <pre> needs rendering here -- bootstrap() would also
    // request another kernel.
    getPageKernel().then((kernel) => {
      if (!isActive) return
      attachCells(kernel)
    }).catch((err) => console.error('Error connecting to the R kernel:', err))

    return () => { isActive = false }
  }, [code])

  useImperativeHandle(ref, () => ({
    setCode(code) {
      setCode(code)
    },
    show() {
      setCodeVisible(true)
    },
    hide() {
      setCodeVisible(false)
    },
  }))

  useEffect(() => {
    if (!dataset.filename) {
      setStatusText('No data')
      return
    }

    let isActive = true

    //excute code in advance on thebelab to import libraries and set dataframe variable
    getPageKernel().then(async (kernel) => {
      if (!isActive) return
      let res = await fetchByJSON('current_data_json', {
        filename: dataset.filename
      })

      let g = await res.json()
      if (!isActive) return
      kernelRef.current = kernel
      const intialcode = getInitialCode(g.data, additional);
      // console.log(intialcode);
      kernel.requestExecute({ code: intialcode });

      dataFrameRaw.current = g.data
      setStatusText('Ready')
    }).catch((err) => {
      if (!isActive) return
      console.error('Error connecting to the R kernel:', err)
      setStatusText('Error connecting to R')
    })

    return () => { isActive = false }
  }, [dataset.filename])

  const runCode = async (e) => {
    try {
      setStatusText('Running the code ...');
      let res = await kernelRef.current.requestExecute({ code: getInitialCode(dataFrameRaw.current,additional) }).done
      document.querySelector('.thebelab-run-button').click()
      setStatusText('Ready');
    } catch (err) {
      console.error("Error during run:", err);
      setStatusText('Error during run');
    }
  }

  useEffect(() => {
    let isActive = true
    getPageKernel().then(() => {
      if (!isActive) return
      setStatusText('Ready')
      let child = document.querySelector('.jp-OutputArea')
      if (child)
        if (document.querySelector('#codesandbox_rightPart'))
          document.querySelector('#codesandbox_rightPart').appendChild(child)
    }).catch((err) => {
      if (!isActive) return
      console.error('Error connecting to the R kernel:', err)
      setStatusText('Error connecting to R')
    })

    return () => { isActive = false }
  }, [])

  return (<Modal
    fixedModalPosition={{ minWidth: '80vw', height: 'auto', margin: 'auto' }}
    zIndex={50}
    isOpen={codeVisible}
    setIsOpen={setCodeVisible}
    onClose={() => {
      setCodeVisible(false)
    }}
  >
    <div>
      <div className='flex flex-col w-full h-full justify-between gap-2 mt-2 mb-4'>
        <div className="flex w-full justify-center items-center m-0 flex-grow-0 h-10">
          <div className="cursor-default">Sandbox status: {statusText}</div>
        </div>
        <div className="flex w-full h-full justify-between flex-grow-1 gap-2">
          <div className="code w-1/2 border-2 m-2 box-content flex flex-col">
            <div className="flex justify-start items-center bg-gray-400">
              <div className='run' onClick={runCode}>Run</div>
            </div>
            <div ref={codeParent}>
            </div>
          </div>
          <div className="result w-1/2 border-2 m-2 box-content" id="codesandbox_rightPart">
          </div>
        </div>

        <div className="flex justify-end items-center w-full px-4 box-border">
          <Button width="w-32" text="Close" onClick={()=>setCodeVisible(false)}></Button>
        </div>
      </div>
    </div>
  </Modal>)
})